import ComponentApi from 'api/components-api'

type Province = { provinceId: number; provinceNameTh: string; provinceNameEn: string }
type District = { districtId: number; districtNameTh: string; districtNameEn: string }
type SubDistrict = { subDistrictId: number; subDistrictNameTh: string; subDistrictNameEn: string; postcode: string }

export type ResolvedThaiAddress = {
  provinceId: number | null
  districtId: number | null
  subDistrictId: number | null
  postcode: string
}

const stripProvincePrefix = (s: string) =>
  s.replace(/^\s*(จังหวัด|จ\.)\s*/, '').trim()
const stripDistrictPrefix = (s: string) =>
  s.replace(/^\s*(อำเภอ|อ\.|เขต)\s*/, '').trim()
const stripSubDistrictPrefix = (s: string) =>
  s.replace(/^\s*(ตำบล|ต\.|แขวง)\s*/, '').trim()

const fuzzyMatch = (a: string, b: string): boolean => {
  if (!a || !b) return false
  const na = a.trim()
  const nb = b.trim()
  if (!na || !nb) return false
  if (na === nb) return true
  return na.includes(nb) || nb.includes(na)
}

const loadProvincesCached = async (): Promise<Province[]> => {
  const cached = sessionStorage.getItem('provinces')
  if (cached) {
    try {
      const items = JSON.parse(cached)
      if (Array.isArray(items) && items.length) return items
    } catch { /* ignore */ }
  }
  const res = await ComponentApi.findAllProvinces()
  if (res.status !== 200) return []
  const items = res.data || []
  try { sessionStorage.setItem('provinces', JSON.stringify(items)) } catch { /* quota */ }
  return items
}

export const resolveThaiAddress = async (
  provinceTextRaw: string,
  districtTextRaw: string,
  subDistrictTextRaw: string,
): Promise<ResolvedThaiAddress> => {
  const result: ResolvedThaiAddress = {
    provinceId: null,
    districtId: null,
    subDistrictId: null,
    postcode: '',
  }

  const provinceText = stripProvincePrefix(provinceTextRaw || '')
  const districtText = stripDistrictPrefix(districtTextRaw || '')
  const subDistrictText = stripSubDistrictPrefix(subDistrictTextRaw || '')

  if (!provinceText) return result

  const provinces = await loadProvincesCached()
  const province = provinces.find(
    (p) => fuzzyMatch(p.provinceNameTh, provinceText) || fuzzyMatch(p.provinceNameEn, provinceText),
  )
  if (!province) return result
  result.provinceId = province.provinceId

  if (!districtText) return result

  const districtsRes = await ComponentApi.findAllDistricts(String(province.provinceId))
  if (districtsRes.status !== 200) return result
  const districts: District[] = districtsRes.data || []
  const district = districts.find(
    (d) => fuzzyMatch(d.districtNameTh, districtText) || fuzzyMatch(d.districtNameEn, districtText),
  )
  if (!district) return result
  result.districtId = district.districtId

  if (!subDistrictText) return result

  const subDistrictsRes = await ComponentApi.findAllSubDistricts(String(district.districtId))
  if (subDistrictsRes.status !== 200) return result
  const subDistricts: SubDistrict[] = subDistrictsRes.data || []
  const subDistrict = subDistricts.find(
    (s) => fuzzyMatch(s.subDistrictNameTh, subDistrictText) || fuzzyMatch(s.subDistrictNameEn, subDistrictText),
  )
  if (!subDistrict) return result
  result.subDistrictId = subDistrict.subDistrictId
  result.postcode = subDistrict.postcode || ''

  return result
}
